import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardCheck, 
  Download, 
  Clock,
  Users,
  Tag,
  CheckSquare,
  MoreVertical,
  Star
} from 'lucide-react';

const ChecklistCard = ({ checklist, onDownload }) => {
  const completedItems = checklist.items.filter(item => item.completed).length;
  const totalItems = checklist.items.length;
  const completionPercentage = Math.round((completedItems / totalItems) * 100) || 0;

  const getFrequencyColor = (frequency) => {
    switch (frequency.toLowerCase()) {
      case 'daily': return 'green';
      case 'weekly': return 'blue';
      case 'monthly': return 'purple';
      default: return 'gray';
    }
  };

  const color = getFrequencyColor(checklist.frequency);

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all overflow-hidden border border-gray-100">
      <div className={`h-2 bg-gradient-to-r from-${color}-500 to-${color}-600`} />
      
      <div className="p-5">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-${color}-50`}>
              <ClipboardCheck className={`w-5 h-5 text-${color}-600`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{checklist.title}</h3>
              <div className="flex items-center gap-2 mt-1">
                <Tag className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-sm text-gray-500">{checklist.category}</span>
              </div>
            </div>
          </div>
          <button className="p-1.5 hover:bg-gray-50 rounded-lg transition-colors">
            <MoreVertical className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm font-medium text-gray-700">{completionPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>

        {/* Preview Items */}
        <div className="space-y-2 mb-4">
          {checklist.items.slice(0, 2).map((item, index) => (
            <div key={index} className="flex items-start gap-2">
              <CheckSquare className={`w-4 h-4 mt-0.5 ${
                item.completed ? 'text-green-600' : 'text-gray-400'
              }`} />
              <span className={`text-sm ${
                item.completed ? 'text-gray-500 line-through' : 'text-gray-700'
              }`}>
                {item.task}
              </span>
            </div>
          ))}
          {checklist.items.length > 2 && (
            <div className="text-sm text-gray-500">
              +{checklist.items.length - 2} more items
            </div>
          )}
        </div>

        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            <span>{checklist.frequency}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Users className="w-4 h-4" />
            <span>{checklist.assignedTo?.join(', ') || 'All Staff'}</span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex gap-2">
            <button
              onClick={() => onDownload(checklist)}
              className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              title="Download PDF"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              title="Add to Favorites"
            >
              <Star className="w-4 h-4" />
            </button>
          </div>
          <Link
            to={`/sop/checklists/${checklist.id}`}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            View Checklist
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ChecklistCard; 